import "@/sentry";
import "@fontsource-variable/inter/index.css";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import React from "react";
import ReactDOM from "react-dom/client";
import { AuthProvider } from "react-oidc-context";
import { AffiliateSelectionProvider } from "@/auth/affiliateSelection";
import { anonymousAuthState } from "@/auth/authState";
import { useAuthStoreSync } from "@/auth/useAuthStoreSync";
import { createUserManager, onSigninCallback } from "@/auth/userManager";
import { RouteErrorBoundary } from "@/components/RouteErrorBoundary";
import { ToastProvider } from "@rumbledirect/ui-v2";
import { createQueryClient } from "@/lib/api/queryClient";
import { MotionProvider } from "@rumbledirect/ui-v2/motion";
import { ThemeProvider } from "@/theme";
import { routeTree } from "./routeTree.gen";
import "./globals.css";

const queryClient = createQueryClient();
const userManager = createUserManager();

const router = createRouter({
  routeTree,
  context: { auth: anonymousAuthState, queryClient },
  defaultPreload: "intent",
  defaultErrorComponent: ({ error }) => <RouteErrorBoundary error={error} />,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// Bridges the live OIDC session into the router. `useAuthStoreSync` derives the
// auth state (and keeps the API client's auth store in sync); passing it through
// the RouterProvider `context` prop makes route guards react to auth changes.
// The affiliate selection it also returns is published as context so the shell's
// switcher can change tenants.
function App(): React.JSX.Element {
  const { authState, affiliateSelection } = useAuthStoreSync();
  return (
    <AffiliateSelectionProvider value={affiliateSelection}>
      <RouterProvider router={router} context={{ auth: authState }} />
    </AffiliateSelectionProvider>
  );
}

ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
  <React.StrictMode>
    <AuthProvider userManager={userManager} onSigninCallback={onSigninCallback}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <MotionProvider>
            <ToastProvider>
              <App />
            </ToastProvider>
          </MotionProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </AuthProvider>
  </React.StrictMode>,
);
